import { TrackEntry, TrackEntryAndData, TrackOrStopEntry, TrackOriginal, TrackSource } from "../types/track";
import { TimelineEntry } from "../types/timeline";

/**
 * Coerces the manual-trip properties of a track feature (source, edited, editable, original, ...)
 * into the typed TrackEntry shape; the remaining properties are passed through unchanged.
 */
export function toTrackEntry<T extends TrackEntry>(properties: GeoJSON.GeoJsonProperties): T {
    const props = (properties || {}) as any;
    const original = props.original && typeof props.original === "object"
        ? {
            startTs: Number(props.original.startTs) || 0,
            endTs: Number(props.original.endTs) || 0,
            distance: Number(props.original.distance) || 0,
            startLat: Number(props.original.startLat) || 0,
            startLng: Number(props.original.startLng) || 0,
            endLat: Number(props.original.endLat) || 0,
            endLng: Number(props.original.endLng) || 0,
            startText: props.original.startText || "",
            endText: props.original.endText || "",
        } as TrackOriginal
        : undefined;
    return {
        ...props,
        source: Number(props.source) === TrackSource.MANUAL ? TrackSource.MANUAL : TrackSource.GPS,
        edited: !!props.edited,
        editable: !!props.editable,
        rejectionNote: props.rejectionNote || "",
        routeSrc: props.routeSrc || "",
        routedDistance: props.routedDistance !== undefined ? Number(props.routedDistance) : undefined,
        routedDuration: props.routedDuration !== undefined ? Number(props.routedDuration) : undefined,
        travelMode: props.travelMode || "",
        creator: props.creator || "",
        createdTs: Number(props.createdTs) || 0,
        updater: props.updater || "",
        updatedTs: Number(props.updatedTs) || 0,
        original,
    } as T;
}

export function retrieveTracksFromGeojson(layer: GeoJSON.FeatureCollection<GeoJSON.Geometry> | undefined): TrackEntryAndData[] {
    const trackFeatures = layer ? layer.features?.filter(feat => feat.geometry.type === "LineString") : [];
    return trackFeatures?.map(feat => {
        return {
            entry: toTrackEntry<TrackEntry>(feat.properties),
            data: "coordinates" in feat.geometry ? feat.geometry.coordinates as [] : [],
        } as TrackEntryAndData;
    });
}

export function retrieveStopsFromGeojson(layer: GeoJSON.FeatureCollection<GeoJSON.Geometry> | undefined): TrackOrStopEntry[] {
    const trackFeatures = layer ? layer.features?.filter(feat => feat?.properties?.kind === "stop") : [];
    return trackFeatures?.map(feat => feat.properties as TrackOrStopEntry);
}

export function retrieveTrackEntriesFromGeojson(layer: GeoJSON.FeatureCollection<GeoJSON.Geometry> | undefined): TrackEntry[] {
    const trackFeatures = layer ? layer.features?.filter(feat => feat.properties?.kind === "track") : [];
    return trackFeatures?.map(feat => toTrackEntry<TrackEntry>(feat.properties));
}

export function retrieveTimelineEntriesFromGeojson(layer: GeoJSON.FeatureCollection<GeoJSON.Geometry> | undefined) {
    return layer && layer.features ? layer.features.map(feat => feat.properties as TimelineEntry) : [];
}

export function retrieveTrackEntriesOrStopsFromGeojson(layer: GeoJSON.FeatureCollection<GeoJSON.Geometry> | undefined): TrackOrStopEntry[] {
    const features = layer?.features?.filter(feat => feat.properties?.kind === "track" || feat.properties?.kind === "stop") || [];
    return features.map(feat => feat.geometry.type === "LineString"
        ? { ...toTrackEntry<TrackOrStopEntry>(feat.properties), coordinates: feat.geometry.coordinates }
        : feat.properties?.kind === "track"
            ? toTrackEntry<TrackOrStopEntry>(feat.properties)
            : feat.properties as TrackOrStopEntry);
}

export function parseLongitude(value: string | number): number | undefined {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (!isNaN(parsed) && isFinite(parsed) && parsed >= -180 && parsed <= 180) {
        return parsed;
    }
    return undefined;
}

export function parseLatitude(value: string | number): number | undefined {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (!isNaN(parsed) && isFinite(parsed) && parsed >= -90 && parsed <= 90) {
        return parsed;
    }
    return undefined;
}

export function extractCoordinates(geojson: any): [number, number][] {
    const coordinates: [number, number][] = [];
    geojson.features.forEach((feature: any) => {
        const geometry = feature.geometry;
        if (!geometry) return;
        switch (geometry.type) {
            case 'Point':
                coordinates.push(geometry.coordinates);
                break;
            case 'MultiPoint':
            case 'LineString':
                geometry.coordinates.forEach((coord: [number, number]) => {
                    coordinates.push(coord);
                });
                break;
            case 'Polygon':
            case 'MultiLineString':
                geometry.coordinates.forEach((line: any) => {
                    line.forEach((coord: [number, number]) => {
                        coordinates.push(coord);
                    });
                });
                break;
            case 'MultiPolygon':
                geometry.coordinates.forEach((polygon: any) => {
                    polygon.forEach((line: any) => {
                        line.forEach((coord: [number, number]) => {
                            coordinates.push(coord);
                        });
                    });
                });
                break;
            default:
                break;
        }
    });
    return coordinates;
}
